import {
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import type { VideoModelOption } from '../types';
import { availableModes, formatNumber } from '../utils';

interface ModelPickerProps {
  models: VideoModelOption[];
  mode: string;
  modelCode: string;
  onChange: (selection: { mode: string; modelCode: string }) => void;
}

/** Mode (cost tier) first, then a model configured by the admin for that mode. */
export const ModelPicker: React.FC<ModelPickerProps> = ({ models, mode, modelCode, onChange }) => {
  const { t } = useTranslation();
  const modes = availableModes(models);
  const modeModels = models.filter((model) => model.mode === mode);

  const selectMode = (nextMode: string) => {
    const first = models.find((model) => model.mode === nextMode);
    if (first) onChange({ mode: nextMode, modelCode: first.modelCode });
  };

  return (
    <div className="space-y-3">
      <div>
        <Typography variant="caption" className="mb-1 block text-gray-500 dark:text-slate-400">
          {t('services.generateVideo.modeLabel')}
        </Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={mode}
          onChange={(_event, next: string | null) => next && selectMode(next)}
        >
          {modes.map((value) => (
            <ToggleButton key={value} value={value} className="normal-case">
              {t(`services.generateVideo.modes.${value}`, { defaultValue: value })}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </div>

      <FormControl fullWidth size="small" className="min-w-0">
        <InputLabel id="generate-video-model">{t('services.generateVideo.modelLabel')}</InputLabel>
        <Select
          labelId="generate-video-model"
          label={t('services.generateVideo.modelLabel')}
          value={modeModels.some((model) => model.modelCode === modelCode) ? modelCode : ''}
          onChange={(event) => onChange({ mode, modelCode: String(event.target.value) })}
          renderValue={(value) => {
            const selected = modeModels.find((model) => model.modelCode === value);
            return selected ? <ModelOptionLabel model={selected} /> : '';
          }}
          MenuProps={{ PaperProps: { className: 'max-w-[calc(100vw-32px)]' } }}
        >
          {modeModels.map((model) => (
            <MenuItem key={`${model.providerCode}:${model.modelCode}`} value={model.modelCode}>
              <ModelOptionLabel model={model} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </div>
  );
};

/** Model name (truncated on narrow screens), resolution chip and per-second price. */
const ModelOptionLabel: React.FC<{ model: VideoModelOption }> = ({ model }) => {
  const { t, i18n } = useTranslation();
  return (
    <span className="flex w-full min-w-0 items-center gap-2">
      <span className="min-w-0 truncate">{model.modelCode}</span>
      {model.resolutionLabel && (
        <Chip size="small" label={model.resolutionLabel} variant="outlined" />
      )}
      <span className="ml-auto shrink-0 text-xs text-gray-500 dark:text-slate-400">
        {t('services.generateVideo.tokensPerSecond', {
          value: formatNumber(model.tokensPerSecond, i18n.language),
        })}
      </span>
    </span>
  );
};
